import { Fragment, useState, useMemo } from 'react';
import { Combobox, Transition } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/outline';
import { OptionChain } from 'constants/opc';
import classNames from 'styles/utils/classNames';
import { getAvailableStrikes } from './utils';

interface Props {
  value: number;
  onChange: (strike: number) => void;
  chain: OptionChain | undefined;
  ticker: string;
  expirationKey: string;
  optionType: 'CALL' | 'PUT';
  spot: number;
}

export default function StrikeCombobox({
  value,
  onChange,
  chain,
  ticker,
  expirationKey,
  optionType,
  spot,
}: Props) {
  const [query, setQuery] = useState('');

  const availableStrikes = useMemo(
    () => getAvailableStrikes(chain, ticker, expirationKey, optionType),
    [chain, ticker, expirationKey, optionType],
  );

  const filteredStrikes = useMemo(() => {
    if (query === '') {
      return availableStrikes;
    }

    return availableStrikes.filter((strike) => strike.toString().includes(query));
  }, [availableStrikes, query]);

  const minStrike = availableStrikes.length > 0 ? Math.min(...availableStrikes) : 0;
  const maxStrike = availableStrikes.length > 0 ? Math.max(...availableStrikes) : 0;

  const showError =
    query !== '' &&
    !filteredStrikes.some((s) => s.toString() === query) &&
    query !== value.toString();

  // Calculate OTM percentage for display
  const calculateOTMPercentage = (strike: number, spotPrice: number): string => {
    const percentage = ((strike - spotPrice) / spotPrice) * 100;
    const sign = percentage > 0 ? '+' : '';
    return `${sign}${percentage.toFixed(1)}%`;
  };

  // Clear input when focusing to show all strikes
  const handleInputFocus = () => {
    setQuery('');
  };

  return (
    <div className="relative">
      <Combobox value={value} onChange={onChange}>
        <div className="relative">
          <div className="relative w-full cursor-default overflow-hidden rounded border border-palette-border bg-palette-black-1 text-left">
            <Combobox.Input
              className="w-full border-none bg-palette-black-1 py-1 pl-2 pr-8 text-sm leading-5 text-white focus:outline-none focus:ring-0"
              displayValue={(strike: number) => (strike === 0 ? '' : strike.toString())}
              onChange={(event) => setQuery(event.target.value)}
              onFocus={handleInputFocus}
              placeholder="Select strike"
            />
            <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
              <ChevronDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
            </Combobox.Button>
          </div>
          <Transition
            as={Fragment}
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
            afterLeave={() => setQuery('')}
          >
            <Combobox.Options className="absolute z-50 mt-1 max-h-52 min-w-[150%] overflow-auto rounded-md bg-palette-black-2 py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
              {filteredStrikes.length === 0 && query !== '' ? (
                showError && availableStrikes.length > 0 ? (
                  <div className="p-2">
                    <div className="rounded border border-yellow-500/30 bg-yellow-500/10 p-2 text-xs">
                      <div className="mb-2 text-yellow-400">
                        {query} is an invalid strike, so you will need to update other parameters if
                        you choose to use it.
                      </div>
                      <div className="mb-2 text-gray-400">
                        Valid strikes range from {minStrike} to {maxStrike}.
                      </div>
                      <button
                        onClick={() => {
                          const invalidStrike = parseFloat(query);
                          if (!isNaN(invalidStrike) && invalidStrike > 0) {
                            onChange(invalidStrike);
                            setQuery('');
                          }
                        }}
                        className="rounded bg-brand-primary px-3 py-1 text-xs font-medium text-white transition-colors hover:bg-brand-primary/80"
                      >
                        Use ${query}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="relative cursor-default select-none px-4 py-2 text-gray-400">
                    No strikes found
                  </div>
                )
              ) : (
                filteredStrikes.map((strike) => (
                  <Combobox.Option
                    key={strike}
                    className={({ active }) =>
                      classNames(
                        'relative cursor-pointer select-none py-2 pl-3 pr-4',
                        active ? 'bg-palette-black-4 text-white' : 'text-gray-200',
                      )
                    }
                    value={strike}
                  >
                    {({ selected, active }) => (
                      <div className="flex items-center justify-between">
                        <span
                          className={classNames(
                            'truncate text-sm',
                            selected ? 'font-bold text-accent-blue' : 'font-medium',
                          )}
                        >
                          {strike}
                        </span>
                        {strike === Math.round(spot) ? (
                          <span
                            className={classNames(
                              'ml-2 text-xs',
                              active ? 'text-gray-300' : 'text-gray-500',
                            )}
                          >
                            ATM
                          </span>
                        ) : (
                          <span
                            className={classNames(
                              'ml-2 text-xs',
                              active ? 'text-gray-300' : 'text-gray-500',
                            )}
                          >
                            {calculateOTMPercentage(strike, spot)}
                          </span>
                        )}
                      </div>
                    )}
                  </Combobox.Option>
                ))
              )}
            </Combobox.Options>
          </Transition>
        </div>
      </Combobox>
    </div>
  );
}
